import React, { useState } from 'react';
import logo from "../reactnest.png";
import { Link } from "react-router-dom";

const TermsPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const termsSections = [
    {
      icon: '📋',
      title: 'License & Usage',
      content: 'React Nest is distributed under the MIT License, granting you extensive rights to use, modify, and distribute the software. You may use React Nest for personal, educational, and commercial projects without restriction. The open source nature ensures freedom while maintaining attribution requirements.'
    },
    {
      icon: '🔧',
      title: 'Software Warranty',
      content: 'React Nest is provided "as is" without warranty of any kind. While we strive for reliability and actively maintain the project, we cannot guarantee uninterrupted service or error-free operation. The open source community continuously works to improve stability and fix issues.'
    },
    {
      icon: '⚖️',
      title: 'Liability Limitations',
      content: 'In no event shall React Nest contributors be liable for any damages arising from the use of this software. This includes but is not limited to data loss, project corruption, or development delays. Users assume responsibility for backing up their work and testing in development environments.'
    },
    {
      icon: '🤝',
      title: 'Community Guidelines',
      content: 'When participating in our community, maintain respectful and constructive communication. Harassment, spam, or malicious behavior will not be tolerated. We foster an inclusive environment where developers of all skill levels can contribute and learn together.'
    },
    {
      icon: '🔄',
      title: 'Contributions & IP',
      content: 'By contributing to React Nest, you grant the project and community the right to use, modify, and distribute your contributions under the MIT License. You retain ownership of your contributions while allowing the project to benefit from your work. All contributions are voluntary and credited.'
    },
    {
      icon: '📦',
      title: 'Third-Party Dependencies',
      content: 'React Nest integrates with various npm packages and external services. Each dependency has its own license terms. We make reasonable efforts to ensure compatibility but recommend reviewing individual package licenses for commercial use or redistribution.'
    }
  ];

  const usageRights = [
    { icon: '✅', text: 'Use for personal and commercial projects' },
    { icon: '✅', text: 'Modify and customize the source code' },
    { icon: '✅', text: 'Distribute modified versions' },
    { icon: '✅', text: 'Create derivative works' },
    { icon: '✅', text: 'Use in proprietary software' },
    { icon: '⚠️', text: 'Must include original license notice' }
  ];

  const restrictions = [
    { icon: '❌', text: 'Remove copyright notices or attribution' },
    { icon: '❌', text: 'Use React Nest name for endorsement without permission' },
    { icon: '❌', text: 'Hold contributors liable for damages' },
    { icon: '❌', text: 'Distribute malicious modifications' },
    { icon: '❌', text: 'Violate dependencies\' license terms' }
  ];

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-gradient-to-br from-purple-600/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 -right-40 w-96 h-96 bg-gradient-to-bl from-blue-600/20 to-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute -bottom-40 left-1/4 w-72 h-72 bg-gradient-to-tr from-pink-500/20 to-purple-600/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      {/* Navigation */}
      <nav className="relative z-50 bg-black/40 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">
                <img src={logo} alt="React Nest Logo" className="w-14 h-14"/>
              </span>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                React Nest
              </span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors duration-300 hover:scale-105 transform">Home</Link>
              <a href="#features" className="text-gray-300 hover:text-white transition-colors duration-300 hover:scale-105 transform">Features</a>
              <a href="#contribute" className="text-gray-300 hover:text-white transition-colors duration-300 hover:scale-105 transform">Contribute</a>
              <a href="#github" className="text-gray-300 hover:text-white transition-colors duration-300 hover:scale-105 transform">GitHub</a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-black/90 backdrop-blur-xl border-t border-white/10">
            <div className="px-6 py-4 space-y-4">
              <a href="#home" className="block text-gray-300 hover:text-white transition-colors">Home</a>
              <a href="#features" className="block text-gray-300 hover:text-white transition-colors">Features</a>
              <a href="#contribute" className="block text-gray-300 hover:text-white transition-colors">Contribute</a>
              <a href="#github" className="block text-gray-300 hover:text-white transition-colors">GitHub</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="space-y-4">
            <h1 className="text-5xl lg:text-6xl font-black leading-tight">
              Terms &
              <span className="block bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                Conditions
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Clear, fair terms that protect both users and contributors while fostering an open source community dedicated to advancing React development.
            </p>
          </div>

          <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
            <span>Effective: September 2025</span>
            <span>•</span>
            <span>MIT License</span>
            <span>•</span>
            <span>Open Source Project</span>
          </div>
        </div>
      </section>

      {/* Terms Sections */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {termsSections.map((section, index) => (
              <div 
                key={index} 
                className="group p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl hover:bg-white/10 transition-all duration-500 hover:scale-105 hover:border-purple-500/50"
              >
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="text-4xl group-hover:scale-110 transition-transform duration-300">
                      {section.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-white">{section.title}</h3>
                  </div>
                  <p className="text-gray-400 leading-relaxed text-lg">{section.content}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rights and Restrictions */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-600/10 to-pink-500/10"></div>
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold">
              Your Rights &
              <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent"> Responsibilities</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Understanding what you can and cannot do with React Nest
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Usage Rights */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
                <span className="text-3xl">✅</span>
                <span>What You Can Do</span>
              </h3>
              <div className="space-y-4">
                {usageRights.map((right, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <span className="text-xl">{right.icon}</span>
                    <span className="text-gray-300">{right.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Restrictions */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
                <span className="text-3xl">🚫</span>
                <span>What You Cannot Do</span>
              </h3>
              <div className="space-y-4">
                {restrictions.map((restriction, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <span className="text-xl">{restriction.icon}</span>
                    <span className="text-gray-300">{restriction.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MIT License Section */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 lg:p-12">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-bold">
                <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">MIT License</span>
              </h2>
              <p className="text-gray-400 mt-4">The foundation of our open source commitment</p>
            </div>

            <div className="bg-gray-900/50 rounded-2xl p-6 border border-white/10 font-mono text-sm">
              <div className="text-gray-300 leading-relaxed">
                <p className="mb-4">MIT License</p>
                <p className="mb-4">Copyright (c) 2025 React Nest Contributors</p>
                <p className="mb-4">
                  Permission is hereby granted, free of charge, to any person obtaining a copy
                  of this software and associated documentation files (the "Software"), to deal
                  in the Software without restriction, including without limitation the rights
                  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
                  copies of the Software, and to permit persons to whom the Software is
                  furnished to do so, subject to the following conditions:
                </p>
                <p className="mb-4">
                  The above copyright notice and this permission notice shall be included in all
                  copies or substantial portions of the Software.
                </p>
                <p className="text-purple-300">
                  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
                  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
                  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Updates & Changes */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold">
            Terms
            <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent"> Updates</span>
          </h2>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
            <div className="space-y-6">
              <p className="text-xl text-gray-300">
                As an open source project, any changes to these terms will be transparently communicated through our GitHub repository and community channels.
              </p>
              <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className="text-3xl mb-2">📢</div>
                  <h4 className="text-lg font-bold text-white">Community Notice</h4>
                  <p className="text-gray-400 text-sm">All changes discussed with the community</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-2">📅</div>
                  <h4 className="text-lg font-bold text-white">30-Day Notice</h4>
                  <p className="text-gray-400 text-sm">Advance notice for major changes</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-2">🔄</div>
                  <h4 className="text-lg font-bold text-white">Version Control</h4>
                  <p className="text-gray-400 text-sm">All changes tracked in Git history</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold">
            Questions About These
            <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent"> Terms</span>?
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Our community-driven approach means we're always available to clarify any aspect of these terms.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 rounded-2xl font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/25">        
              <span className="flex items-center justify-center space-x-3">
                <span>📧</span>
                <span>Email Us</span>
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-white/10 py-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">
                <img src={logo} alt="React Nest Logo" className="w-14 h-14"/>
              </span>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                React Nest
              </span>
            </div>
            
            <div className="flex space-x-8 text-gray-400">
                 <Link to="/privacy" className="hover:text-white transition-colors">
                    Privacy
                </Link>
                <Link to="/terms" className="hover:text-white transition-colors">
                    Terms
                </Link>
                <Link to="/contribute" className="hover:text-white transition-colors">
                    Contribute
                </Link>
                <Link to="/contact" className="hover:text-white transition-colors">
                 Contact
                </Link>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-gray-400">
            <p>&copy; 2025 React Nest. Open Source • MIT License • Community Driven</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;